<template>
  <form class="bzc-form" :class="{ 'is-saved': saved }" novalidate data-test="ask-bizminer-form" @submit.prevent="onSubmit">
    <div class="bzc-form-head">
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"></path></svg>
      <span>{{ form.product }}: your figures</span>
    </div>

    <div class="bzc-form-body">
      <div v-if="subtitle" class="bzc-confirm-product" data-test="ask-bizminer-form-slice">{{ subtitle }}</div>
      <p class="bzc-confirm-note">
        Enter your own figures, in whole dollars. Leave a field blank if you don't have it. <span class="bzc-req">*</span> marks what is required.
      </p>

      <div v-if="saved" class="bzc-confirm-done" role="status" data-test="ask-bizminer-form-saved">
        <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true" fill="#2D6731"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"></path></svg>
        Saved to your Bizminer account. Change a figure and submit again to update it.
      </div>

      <div v-if="banner.length" class="bzc-form-banner" role="alert" data-test="ask-bizminer-form-errors">
        <p v-for="(b, i) in banner" :key="i">{{ b }}</p>
      </div>

      <fieldset v-for="s in sections" :key="s.scope + ':' + s.id" class="bzc-fs" :disabled="submitting" :data-section="s.id">
        <legend class="bzc-fs-title">
          <button type="button" class="bzc-fs-toggle" :aria-expanded="isOpen(s) ? 'true' : 'false'" @click="toggle(s)">
            <svg class="bzc-fs-chev" :class="{ 'is-open': isOpen(s) }" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"></path></svg>
            {{ s.title }}
            <span v-if="sectionErrors(s)" class="bzc-fs-bad">{{ sectionErrors(s) }} to fix</span>
          </button>
        </legend>

        <div v-show="isOpen(s)" class="bzc-fs-body">
          <div v-for="f in s.fields" :key="f.key" class="bzc-fld" :class="{ 'is-company': f.scope === 'company' }" :data-field="f.key">
            <label class="bzc-fld-label" :for="f.scope === 'company' ? idOf(f) : null">
              {{ f.title }}<span v-if="f.isRequired" class="bzc-req" title="Required">*</span>
            </label>
            <p v-if="f.description" class="bzc-hint">{{ f.description }}</p>

            <!-- Yearly figures: one input per year, side by side where there is room, stacked on a phone. -->
            <div v-if="f.scope === 'year'" class="bzc-years">
              <div v-for="y in form.years" :key="y" class="bzc-cell">
                <label class="bzc-year" :for="idOf(f, y)">{{ y }}</label>
                <div class="bzc-cell-main">
                  <div class="bzc-input" :class="{ 'has-error': !!errorOf(f, y) }">
                    <span v-if="f.prefix" class="bzc-pre" aria-hidden="true">{{ f.prefix }}</span>
                    <input
                      :id="idOf(f, y)"
                      type="text"
                      inputmode="decimal"
                      autocomplete="off"
                      :value="valueOf(f, y)"
                      :aria-label="f.title + ', ' + y"
                      :aria-invalid="errorOf(f, y) ? 'true' : null"
                      :aria-describedby="errorOf(f, y) ? idOf(f, y) + '-err' : null"
                      :readonly="submitting"
                      @input="onInput(f, y, $event.target.value)"
                      @blur="onBlur(f, y)"
                    />
                  </div>
                  <p v-if="errorOf(f, y)" :id="idOf(f, y) + '-err'" class="bzc-err" data-test="ask-bizminer-form-field-error">{{ errorOf(f, y) }}</p>
                </div>
              </div>
            </div>

            <!-- Company details: one value. -->
            <div v-else class="bzc-company">
              <div v-if="f.type === 'boolean'" class="bzc-seg" role="group" :aria-label="f.title">
                <button type="button" class="bzc-seg-btn" :class="{ on: valueOf(f) === true }" :aria-pressed="valueOf(f) === true ? 'true' : 'false'" @click="pick(f, true)">Yes</button>
                <button type="button" class="bzc-seg-btn" :class="{ on: valueOf(f) === false }" :aria-pressed="valueOf(f) === false ? 'true' : 'false'" @click="pick(f, false)">No</button>
              </div>
              <div v-else class="bzc-input" :class="{ 'has-error': !!errorOf(f) }">
                <input
                  :id="idOf(f)"
                  type="text"
                  autocomplete="off"
                  :inputmode="f.type === 'scale' ? 'decimal' : null"
                  :maxlength="f.type === 'text' ? f.max || 100 : 12"
                  :placeholder="f.type === 'scale' ? scalePlaceholder(f) : ''"
                  :value="valueOf(f)"
                  :aria-invalid="errorOf(f) ? 'true' : null"
                  :aria-describedby="errorOf(f) ? idOf(f) + '-err' : null"
                  :readonly="submitting"
                  @input="onInput(f, null, $event.target.value)"
                />
              </div>
              <p v-if="errorOf(f)" :id="idOf(f) + '-err'" class="bzc-err" data-test="ask-bizminer-form-field-error">{{ errorOf(f) }}</p>
              <p v-else-if="f.key === 'company.name' && lockNote" class="bzc-hint" data-test="ask-bizminer-form-lock">{{ lockNote }}</p>
            </div>
          </div>
        </div>
      </fieldset>

      <div class="bzc-form-actions">
        <button type="submit" class="bzc-btn bzc-btn-primary" :disabled="submitting || busy" data-test="ask-bizminer-form-submit">
          <svg v-if="submitting" class="bzc-spin" width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="#fff" stroke-width="3"><path d="M12 3a9 9 0 1 1-9 9"></path></svg>
          {{ submitting ? 'Saving…' : form.reportId != null ? 'Update my figures' : 'Create ' + form.product }}
        </button>
      </div>
      <div class="bzc-fine">Your figures are saved in your Bizminer account. Creating the report is free. Its first run uses one of your subscription's profiles and reports, and you confirm that first.</div>
    </div>
  </form>
</template>

<script>
import {
  formSections,
  formSubtitle,
  valuationLockNote,
  draftStorageKey,
  serializeDraft,
  forgetDraft,
  parseStoredDraft,
  chooseDraft,
  setDraftValue,
  formatDraftNumber,
  checkDraft,
  buildSubmitValues,
  fieldErrorFor,
  unmappedFormErrors,
} from './chatCore';

// The input form of a Financial, Valuation or Market Report (CHAT-11/b, /d, CHAT-22).
// Presentational: it owns what is typed (a draft kept per form in sessionStorage so a
// reload keeps it) and hands the finished values up; the panel submits them.
export default {
  name: 'chat-form-card',
  props: {
    form: { type: Object, required: true },
    uid: { type: [Number, String], default: null },
    busy: { type: Boolean, default: false }, // something else is running in the panel
  },
  data() {
    return {
      draft: chooseDraft(this.form, parseStoredDraft(this.readDraft(), this.form)),
      serverErrors: { ...this.form.errors },
      localErrors: {},
      openMap: {},
    };
  },
  computed: {
    sections() {
      return formSections(this.form);
    },
    subtitle() {
      return formSubtitle(this.form);
    },
    lockNote() {
      return valuationLockNote(this.form);
    },
    submitting() {
      return this.form.phase === 'submitting';
    },
    saved() {
      return this.form.result === 'saved' || (this.form.status === 'submitted' && !this.form.message && !Object.keys(this.errors).length);
    },
    errors() {
      return { ...this.serverErrors, ...this.localErrors };
    },
    // What the form says as a whole: the service's message, then errors that name no visible field.
    banner() {
      const out = [];
      // A message that is already on its own field (the company name lock) is not said twice.
      const onField = Object.keys(this.errors).some((k) => this.errors[k] === this.form.message && !unmappedFormErrors(this.form, { [k]: this.errors[k] }).length);
      if (this.form.message && !onField) out.push(this.form.message);
      unmappedFormErrors(this.form, this.errors).forEach((m) => {
        if (!out.includes(m)) out.push(m);
      });
      return out;
    },
  },
  watch: {
    // A new answer from the service replaces the old errors, and the first one is brought into view.
    'form.errors'(e) {
      this.serverErrors = { ...e };
      this.localErrors = {};
      if (Object.keys(e).length) this.focusFirstError();
    },
    draft: {
      deep: true,
      handler(d) {
        const key = this.storageKey();
        if (!key) return; // no user: nothing is stored
        try {
          sessionStorage.setItem(key, serializeDraft(d, Date.now(), this.form.updatedAt));
        } catch (e) {
          /* storage unavailable: a reload just won't keep what was typed */
        }
      },
    },
    // Saved: the service holds these figures now, so the typed copy is not kept on this device. A new submission
    // resets the result (chatCore 'formPhase'), so this fires after every successful save, not just the first.
    'form.result'(r) {
      if (r !== 'saved') return;
      try {
        forgetDraft(sessionStorage, this.uid, this.form.id);
      } catch (e) {
        /* storage unavailable: nothing was kept */
      }
    },
  },
  methods: {
    // null with no user: drafts are never kept under a shared key.
    storageKey() {
      return draftStorageKey(this.uid, this.form.id);
    },
    readDraft() {
      const key = draftStorageKey(this.uid, this.form.id);
      if (!key) return null;
      try {
        return sessionStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    idOf(f, y) {
      return ('bzf-' + this.form.id + '-' + f.key + (y ? '-' + y : '')).replace(/[^a-zA-Z0-9_-]/g, '_');
    },
    valueOf(f, y) {
      if (f.scope === 'company') {
        const v = this.draft.company[f.prop];
        return v == null ? (f.type === 'boolean' ? null : '') : v;
      }
      const row = this.draft.inputs[y];
      return row && row[f.prop] != null ? row[f.prop] : '';
    },
    errorOf(f, y) {
      return fieldErrorFor(this.errors, f, y);
    },
    pathOf(f, y) {
      return f.scope === 'company' ? 'company.' + f.prop : 'inputs.' + y + '.' + f.prop;
    },
    // Editing a field clears its own error.
    dismiss(path) {
      if (path in this.serverErrors) {
        const e = { ...this.serverErrors };
        delete e[path];
        this.serverErrors = e;
      }
      if (path in this.localErrors) {
        const e = { ...this.localErrors };
        delete e[path];
        this.localErrors = e;
      }
    },
    onInput(f, y, value) {
      this.draft = setDraftValue(this.draft, f, y, value);
      this.dismiss(this.pathOf(f, y));
    },
    onBlur(f, y) {
      const v = this.valueOf(f, y);
      const tidy = formatDraftNumber(v);
      if (tidy !== v) this.draft = setDraftValue(this.draft, f, y, tidy);
    },
    // Yes / No: pressing the chosen one again clears the answer.
    pick(f, v) {
      this.draft = setDraftValue(this.draft, f, null, this.valueOf(f) === v ? null : v);
      this.dismiss(this.pathOf(f, null));
    },
    scalePlaceholder(f) {
      return (f.min != null ? f.min : 0) + ' to ' + (f.max != null ? f.max : 10);
    },
    // Sections with something to fix, or something required, start open; the rest fold away.
    isOpen(s) {
      if (this.sectionErrors(s)) return true;
      const k = s.scope + ':' + s.id;
      if (k in this.openMap) return this.openMap[k];
      return this.sections[0] === s || s.scope === 'company' || s.fields.some((f) => f.isRequired);
    },
    toggle(s) {
      this.openMap = { ...this.openMap, [s.scope + ':' + s.id]: !this.isOpen(s) };
    },
    sectionErrors(s) {
      let n = 0;
      s.fields.forEach((f) => {
        if (f.scope === 'company') n += this.errorOf(f) ? 1 : 0;
        else this.form.years.forEach((y) => (n += this.errorOf(f, y) ? 1 : 0));
      });
      return n;
    },
    onSubmit() {
      if (this.submitting || this.busy) return;
      const local = checkDraft(this.form, this.draft);
      this.localErrors = local;
      this.serverErrors = {};
      if (Object.keys(local).length) {
        this.focusFirstError();
        return;
      }
      this.$emit('submit', this.form, buildSubmitValues(this.form, this.draft));
    },
    focusFirstError() {
      this.$nextTick(() => {
        const el = this.$el && this.$el.querySelector ? this.$el.querySelector('.has-error input, .bzc-form-banner') : null;
        if (!el) return;
        if (el.scrollIntoView) el.scrollIntoView({ block: 'center' });
        if (el.focus && el.tagName === 'INPUT') el.focus({ preventScroll: true });
      });
    },
  },
};
</script>

<style lang="scss">
$bzc-navy: #003666;
$bzc-cyan: #0cbcf2;

.bzc-form {
  border: 1px solid #b8cde0; border-radius: 10px; background: #fff; overflow: hidden; box-shadow: 0 1px 3px rgba(0, 30, 60, 0.08);
  &.is-saved { border-color: #b7d8ba; }
}
.bzc-form-head { display: flex; align-items: center; gap: 8px; padding: 9px 14px; background: $bzc-navy; color: #fff; font-size: 13px; font-weight: 700; }
.bzc-form.is-saved .bzc-form-head { background: #2d6731; }
.bzc-form-body { padding: 12px 14px 14px; display: flex; flex-direction: column; gap: 12px; }
.bzc-form-banner { font-size: 12.5px; line-height: 1.45; color: #a3261c; background: #fdf0ee; border: 1px solid #f1c9c4; border-radius: 8px; padding: 8px 10px; p + p { margin-top: 4px; } }

.bzc-req { color: #a3261c; margin-left: 2px; font-weight: 700; }

.bzc-fs { border: 0; padding: 0; margin: 0; min-width: 0; border-top: 1px solid #e6ebf1; padding-top: 6px; }
.bzc-form legend.bzc-fs-title { display: block; padding: 0; margin: 0; width: 100%; border: 0; font-size: 13.5px; line-height: 1.3; }
// `.bzc-panel button { font: inherit }` is more specific than a bare class: repeat the form class.
.bzc-form .bzc-fs-toggle {
  display: flex; align-items: center; gap: 6px; width: 100%; min-height: 36px; padding: 0; border: 0; background: transparent; text-align: left;
  font-family: Inter, Roboto, sans-serif; font-size: 13.5px; font-weight: 700; color: $bzc-navy;
  &:focus-visible { outline: 2px solid $bzc-cyan; outline-offset: 2px; }
}
.bzc-fs-chev { flex: 0 0 auto; transition: transform 0.12s; &.is-open { transform: rotate(90deg); } }
.bzc-fs-bad { margin-left: auto; font-size: 11.5px; font-weight: 700; color: #a3261c; }
.bzc-fs-body { display: flex; flex-direction: column; gap: 14px; padding: 4px 0 8px; }

.bzc-fld { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.bzc-fld-label { margin: 0; font-size: 12.5px; font-weight: 700; color: #13213c; }
.bzc-hint { font-size: 11.5px; line-height: 1.4; color: #5b6575; }

// One column per year where there is room; the phone rule below stacks them.
.bzc-years { display: grid; grid-template-columns: repeat(auto-fit, minmax(92px, 1fr)); gap: 8px; }
.bzc-cell { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.bzc-cell-main { min-width: 0; }
.bzc-year { margin: 0; font-size: 11px; font-weight: 700; color: #5b6575; letter-spacing: 0.03em; }

.bzc-input {
  display: flex; align-items: center; min-width: 0; height: 36px; border: 1px solid #b9c6d4; border-radius: 6px; background: #fff; padding: 0 6px;
  &:focus-within { border-color: $bzc-cyan; box-shadow: 0 0 0 2px rgba(12, 188, 242, 0.2); }
  &.has-error { border-color: #c4382c; background: #fffafa; }
  input { flex: 1 1 auto; min-width: 0; width: 100%; height: 100%; border: 0; outline: none; background: transparent; font: inherit; font-size: 13px; color: #13213c; padding: 0; }
}
.bzc-pre { flex: 0 0 auto; margin-right: 3px; font-size: 12px; color: #5b6575; }
.bzc-err { margin: 3px 0 0; font-size: 11.5px; line-height: 1.35; color: #a3261c; }
.bzc-company .bzc-input { max-width: 100%; }

.bzc-seg { display: inline-flex; align-self: flex-start; border: 1px solid #b9c6d4; border-radius: 6px; overflow: hidden; }
.bzc-form .bzc-seg-btn {
  min-width: 64px; height: 36px; padding: 0 14px; border: 0; background: #fff; color: $bzc-navy; font-size: 13px; font-weight: 500;
  &:not(:first-child) { border-left: 1px solid #b9c6d4; }
  &.on { background: $bzc-navy; color: #fff; }
  &:focus-visible { outline: 2px solid $bzc-cyan; outline-offset: -2px; }
}

.bzc-form-actions { display: flex; }

// Phone: the years stack, each with its label beside the input; 16px text so iOS does not zoom; 44px targets.
@media (max-width: 600px) {
  .bzc-years { grid-template-columns: 1fr; gap: 6px; }
  .bzc-cell { display: grid; grid-template-columns: 44px minmax(0, 1fr); align-items: start; column-gap: 8px; }
  .bzc-year { padding-top: 13px; }
  .bzc-input { height: 44px; padding: 0 10px; input { font-size: 16px; } }
  .bzc-pre { font-size: 15px; }
  .bzc-form .bzc-fs-toggle { min-height: 44px; }
  .bzc-form .bzc-seg-btn { height: 44px; min-width: 80px; }
  .bzc-form-actions .bzc-btn { flex: 1 1 auto; }
}
</style>
